import get from 'lodash/get';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import SelectDocumentInGroup from '~/components/SearchV2/AddSearchResultsToGroupV2/SelectDocumentInGroup/SelectDocumentInGroup';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { AddToGroupStepType } from '~/enums/AddToGroupType';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { FormattedMessage, useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { MODAL_ADD_DOCUMENTS_TO_GROUP } from '~/types/modal.types';
import { ERROR, SUCCESS } from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import Text from '~/ui/components/Text/Text';
import WizardPageLayout from '~/ui/components/WizardPageLayout/WizardPageLayout';

import SearchDocumentsToAddToGroup from '../SearchDocumentsToAddToGroup/SearchDocumentsToAddToGroup';

const MAX_TITLE_WIDTH = 355;

const AddDocuments = ({ groupId, groupName }) => {
  const intl = useIntl();
  const modal = useSelector(slices.documentGroup.selectors.selectModal);
  const { modalType } = modal || {};
  const step = useSelector(
    slices.documentGroup.selectors.currentAddToGroupStep,
  );
  const documentId = useSelector(
    slices.documentGroup.selectors.selectDocumentId,
  );
  const documentIds = useSelector(
    slices.documentGroup.selectors.selectDocumentIds,
  );
  const shouldNextDisabled = !documentIds || documentIds?.length === 0;

  const stepsTitle = intl.formatMessage({
    id: 'AddDocuments.addToGroupTitle',
    defaultMessage: 'Add to group',
    description: 'Title for adding documents to group step',
  });

  const dispatch = useDispatch();

  const [addDocumentsToGroup, addDocumentsToGroupResult] =
    api.endpoints.addDocumentsToGroup.useMutation();
  const { isLoading: addDocumentsToGroupLoading } = addDocumentsToGroupResult;

  const [addDocumentsToGroupLinkParent, addDocumentsToGroupLinkParentResult] =
    api.endpoints.addDocumentsToGroupLinkParent.useMutation();
  const { isLoading: addDocumentsToGroupLinkParentLoading } =
    addDocumentsToGroupLinkParentResult;

  const resetModal = () => {
    dispatch(slices.documentGroup.actions.setDocumentIds(undefined));
    dispatch(slices.documentGroup.actions.setDocumentId(undefined));
    dispatch(
      slices.documentGroup.actions.setCurrentAddToGroupStep(
        AddToGroupStepType.DOCUMENTS,
      ),
    );
  };

  const handleHideModal = () => {
    dispatch(slices.documentGroup.actions.setModal());
    resetModal();
  };

  const handleSelectDocumentIds = useCallback((docIds) => {
    dispatch(slices.documentGroup.actions.setDocumentIds(docIds));
  }, []);

  const handleLinkDocumentsInGroup = useCallback((documentId) => {
    dispatch(slices.documentGroup.actions.setDocumentId(documentId));
  }, []);

  const handleAddDocumentsToGroup = () => {
    (documentId
      ? addDocumentsToGroupLinkParent({
          groupId,
          parentId: documentId,
          documentIds,
        })
      : addDocumentsToGroup({ groupId, documentIds })
    )
      .unwrap()
      .then(() => {
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'AddDocuments.addSuccess',
            defaultMessage: 'Documents added to the group successfully.',
            description: 'Success message when documents are added to group',
          }),
        );
      })
      .catch((error) => {
        const { response } = error;
        const defaultErrorMessage = intl.formatMessage({
          id: 'AddDocuments.addError',
          defaultMessage:
            'An error occurred while adding documents to the group, please try again later.',
          description: 'Default error message when adding documents fails',
        });
        if (response.status === HttpStatusCodeType.BadRequest) {
          showToast(ERROR, get(response, 'data.message', defaultErrorMessage));
        } else {
          showToast(ERROR, defaultErrorMessage);
        }
      })
      .finally(() => {
        dispatch(slices.documentGroup.actions.setDocumentIds(undefined));
        dispatch(slices.documentGroup.actions.setDocumentId(undefined));
        handleHideModal();
      });
  };

  const goToStructureStep = () => {
    dispatch(
      slices.documentGroup.actions.setCurrentAddToGroupStep(
        AddToGroupStepType.STRUCTURE,
      ),
    );
  };

  const steps = useMemo(() => {
    return {
      [AddToGroupStepType.DOCUMENTS]: {
        title: stepsTitle,
        component: (
          <>
            <FlexLayout justifyContent="center">
              <Text variant="subtitle">
                <FormattedMessage
                  id="AddDocuments.selectDocumentsMessage"
                  defaultMessage='Select documents you want to add to the group: "{groupName}" '
                  description="Instructions for selecting documents to add to group. Example: groupName = 'Project A'"
                  values={{ groupName }}
                />
              </Text>
            </FlexLayout>
            <SearchDocumentsToAddToGroup
              onDocumentsSelect={handleSelectDocumentIds}
            />
          </>
        ),
        action: {
          disabled: shouldNextDisabled,
          promise: () => goToStructureStep(),
          text: intl.formatMessage({
            id: 'AddDocuments.nextButton',
            defaultMessage: 'Next',
            description: 'Button text for next step',
          }),
        },
      },
      [AddToGroupStepType.STRUCTURE]: {
        title: stepsTitle,
        component: (
          <SelectDocumentInGroup
            group={groupId}
            onDocumentSelected={handleLinkDocumentsInGroup}
          />
        ),
        action: {
          text: intl.formatMessage({
            id: 'AddDocuments.addToGroupButton',
            defaultMessage: 'Add to group',
            description: 'Button text for adding to group',
          }),
        },
      },
    };
  }, [groupId, shouldNextDisabled, groupName, intl]);

  const isLoading =
    addDocumentsToGroupLoading || addDocumentsToGroupLinkParentLoading;

  const buttonTitle = documentId
    ? intl.formatMessage({
        id: 'AddDocuments.addAsLinkedButton',
        defaultMessage: 'Add to group as linked documents',
        description: 'Button text for adding as linked documents',
      })
    : steps[step]?.action.text;
  const handleActionPromise =
    step === AddToGroupStepType.STRUCTURE
      ? handleAddDocumentsToGroup
      : steps[step]?.action.promise;

  if (modalType === MODAL_ADD_DOCUMENTS_TO_GROUP) {
    return (
      <WizardPageLayout
        currentStep={step}
        steps={[AddToGroupStepType.DOCUMENTS, AddToGroupStepType.STRUCTURE]}
        title={{ text: steps[step]?.title, maxWidth: MAX_TITLE_WIDTH }}
        onClose={handleHideModal}
        actionButton={{
          ...steps[step]?.action,
          text: buttonTitle,
          promise: handleActionPromise,
        }}
        backButton={{
          text: intl.formatMessage({
            id: 'AddDocuments.cancelButton',
            defaultMessage: 'Cancel',
            description: 'Button text for canceling',
          }),
          variant: 'tertiary',
          promise: () => handleHideModal(),
        }}
        isContentFill={true}
      >
        <LoadingContainer isLoading={isLoading}>
          {steps[step]?.component}
        </LoadingContainer>
      </WizardPageLayout>
    );
  } else {
    return null;
  }
};

export default AddDocuments;
